import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, ArrowRightLeft, Zap, History, Wallet, X, IndianRupee } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDate } from "@/lib/api";

export default function SavingWallet() {
  const nav = useNavigate();
  const [bal, setBal] = useState(0);
  const [bonusBal, setBonusBal] = useState(0);
  const [tx, setTx] = useState([]);
  const [autopay, setAutopay] = useState(false);

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferAmt, setTransferAmt] = useState("");
  const [transferring, setTransferring] = useState(false);

  const [autopayOpen, setAutopayOpen] = useState(false);

  const load = async () => {
    const w = await api.get("/customer/wallet");
    setBal(w.data.saving_balance);
    setBonusBal(w.data.bonus_balance);
    const t = await api.get("/customer/transactions?wallet=saving");
    setTx(t.data);
    try {
      const c = await api.get("/customer/collections");
      setAutopay(!!c.data.autopay_enabled);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const submitTransfer = async () => {
    const amt = parseFloat(transferAmt);
    if (!amt || amt <= 0) { toast.error("Enter valid amount"); return; }
    if (amt > bonusBal) { toast.error("Insufficient bonus balance"); return; }
    setTransferring(true);
    try {
      await api.post("/customer/wallet/transfer-bonus-to-saving", { amount: amt });
      toast.success(`${fmtINR(amt)} moved Bonus → Saving`);
      setTransferOpen(false); setTransferAmt(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setTransferring(false); }
  };

  const toggleAutopay = async () => {
    try {
      await api.patch("/customer/autopay", { enabled: !autopay });
      toast.success(`AutoPay turned ${!autopay ? "ON" : "OFF"}`);
      setAutopay(!autopay);
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="saving-wallet-page">
      <Header title="Saving Wallet" />
      <div className="px-5 -mt-12 relative z-10">
        <div className="saving-wallet-grad text-white rounded-3xl p-6 card-shadow-lg">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] opacity-90">
            <Wallet className="w-3.5 h-3.5" /> Saving Wallet
          </div>
          <div className="mt-3 text-[11px] opacity-80">Available Balance</div>
          <div data-testid="saving-balance" className="text-3xl font-extrabold tracking-tight mt-1">{fmtINR(bal)}</div>
          <div className="text-[11px] opacity-80 mt-2">Daily Collection & Deposits</div>
        </div>
      </div>

      <div className="px-5 mt-5 grid grid-cols-4 gap-2">
        <ActionBtn icon={Plus} label="Add Money" onClick={() => nav("/customer/add-money")} testId="action-add-money" tone="blue" />
        <ActionBtn icon={ArrowRightLeft} label="Transfer" onClick={() => setTransferOpen(true)} testId="action-transfer" tone="violet" sub={`Bonus: ${fmtINR(bonusBal)}`} />
        <ActionBtn icon={Zap} label="AutoPay" onClick={() => setAutopayOpen(true)} testId="action-autopay" tone={autopay ? "emerald" : "slate"} sub={autopay ? "ON" : "OFF"} />
        <ActionBtn icon={History} label="History" onClick={() => nav("/customer/transactions")} testId="action-history" tone="amber" />
      </div>

      <div className="px-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-extrabold tracking-tight">Recent Transactions</h3>
          <button onClick={() => nav("/customer/transactions")} data-testid="view-all-tx" className="text-xs text-tenant-primary font-bold">View All</button>
        </div>
        <div className="space-y-2.5">
          {tx.slice(0, 8).map((t) => (
            <TxRow key={t.id} tx={t} />
          ))}
          {tx.length === 0 && <Empty msg="No transactions yet." />}
        </div>
      </div>
      <div className="h-6" />

      {/* Transfer Modal */}
      {transferOpen && (
        <div data-testid="saving-transfer-modal" className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setTransferOpen(false)}>
          <div className="bg-white rounded-3xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-extrabold text-lg">Transfer from Bonus</div>
              <button onClick={() => setTransferOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-slate-500 mt-1">Bonus available: <b>{fmtINR(bonusBal)}</b></div>
            <label className="mt-3 block text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Amount</label>
            <div className="mt-1.5 flex items-center gap-2 border-2 border-slate-200 rounded-2xl px-4 py-3">
              <IndianRupee className="w-5 h-5 text-slate-400" />
              <input data-testid="saving-transfer-amount" value={transferAmt} onChange={(e) => setTransferAmt(e.target.value.replace(/[^\d.]/g, ""))} placeholder="0" inputMode="decimal" className="flex-1 outline-none text-xl font-extrabold" />
              <button onClick={() => setTransferAmt(String(bonusBal))} className="text-[11px] bg-slate-100 text-slate-600 font-bold px-2 py-1 rounded">MAX</button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button onClick={() => setTransferOpen(false)} className="py-3 rounded-xl border border-slate-200 font-bold">Cancel</button>
              <button data-testid="saving-confirm-transfer" onClick={submitTransfer} disabled={transferring} className="py-3 rounded-xl bg-violet-600 text-white font-bold disabled:opacity-60">{transferring ? "..." : "Transfer"}</button>
            </div>
          </div>
        </div>
      )}

      {/* AutoPay Modal */}
      {autopayOpen && (
        <div data-testid="autopay-modal" className="fixed inset-0 bg-slate-900/50 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setAutopayOpen(false)}>
          <div className="bg-white rounded-3xl p-5 max-w-sm w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-extrabold text-lg flex items-center gap-2"><Zap className="w-5 h-5 text-emerald-500" /> AutoPay Setup</div>
              <button onClick={() => setAutopayOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <div className="text-sm text-slate-600 mt-3">
              When AutoPay is ON, your premium will be paid automatically from this Saving Wallet on the due date.
            </div>
            <div className="mt-4 bg-slate-50 rounded-2xl p-4 flex items-center justify-between">
              <div>
                <div className="font-extrabold">AutoPay Premium</div>
                <div className="text-[11px] text-slate-500">From Saving Wallet</div>
              </div>
              <button
                data-testid="saving-autopay-toggle"
                onClick={toggleAutopay}
                className={`w-12 h-7 rounded-full relative transition-colors ${autopay ? "bg-emerald-600" : "bg-slate-300"}`}
              >
                <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${autopay ? "left-5" : "left-0.5"}`} />
              </button>
            </div>
            <button onClick={() => setAutopayOpen(false)} className="w-full mt-4 bg-tenant-primary text-white py-3 rounded-2xl font-bold">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

const Header = ({ title }) => {
  const nav = useNavigate();
  return (
    <div className="bg-tenant-gradient text-white px-5 pt-6 pb-20 rounded-b-[2rem] relative">
      <div className="flex items-center gap-3">
        <button data-testid="header-back" onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-white/15 grid place-items-center">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="font-extrabold tracking-tight text-lg">{title}</div>
      </div>
    </div>
  );
};

const ActionBtn = ({ icon: Icon, label, onClick, testId, tone = "blue", sub }) => {
  const tones = {
    blue: "bg-blue-50 text-tenant-primary",
    violet: "bg-violet-50 text-violet-600",
    emerald: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    slate: "bg-slate-100 text-slate-500",
  };
  return (
    <button data-testid={testId} onClick={onClick} className="flex flex-col items-center gap-1.5 bg-white rounded-2xl py-3 card-shadow tap-scale">
      <div className={`w-10 h-10 rounded-xl ${tones[tone]} grid place-items-center`}>
        <Icon className="w-5 h-5" />
      </div>
      <span className="text-[10px] font-semibold text-slate-700">{label}</span>
      {sub && <span className="text-[9px] text-slate-400 font-bold -mt-1">{sub}</span>}
    </button>
  );
};

const TxRow = ({ tx }) => {
  const credit = tx.type === "credit";
  return (
    <div data-testid={`tx-row-${tx.id}`} className="bg-white border border-slate-100 rounded-2xl p-3 flex items-center justify-between card-shadow">
      <div>
        <div className="font-bold text-sm capitalize">{tx.category.replaceAll("_", " ")}</div>
        <div className="text-[11px] text-slate-400">{fmtDate(tx.created_at)}</div>
      </div>
      <div className={`font-extrabold ${credit ? "text-emerald-600" : "text-rose-500"}`}>
        {credit ? "+" : "-"}{fmtINR(tx.amount)}
      </div>
    </div>
  );
};

const Empty = ({ msg }) => (
  <div className="text-center text-slate-400 text-sm py-10">{msg}</div>
);
